import { Fragment } from 'react'
import { Dialog, DialogPanel, DialogTitle, Transition } from '@headlessui/react'
import { XMarkIcon, BellIcon } from '@heroicons/react/24/outline'
import { formatNotificationTime } from '../lib/notificationService'

interface NotificationsModalProps {
  open: boolean
  onClose: () => void
  notifications: any[]
  loading: boolean
  markAsRead?: (notificationId: string) => Promise<void>
  onNavigate?: (view: string) => void
  markAllAsRead?: () => Promise<void>
  userTimezone?: string
}

export default function NotificationsModal({ open, onClose, notifications, loading, markAsRead, onNavigate, markAllAsRead, userTimezone }: NotificationsModalProps) {
  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel className="w-full max-w-2xl max-h-[80vh] transform overflow-hidden rounded-3xl bg-white dark:bg-[#1a1a1e] shadow-2xl transition-all border border-gray-100 dark:border-white/10">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-white/10">
                  <DialogTitle className="text-xl font-semibold text-gray-900 dark:text-white">
                    All Notifications
                  </DialogTitle>
                  <div className="flex items-center space-x-3">
                    {notifications.length > 0 && notifications.some(n => !n.read) && markAllAsRead && (
                      <button
                        onClick={async () => {
                          await markAllAsRead();
                        }}
                        className="inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium text-indigo-600 dark:text-purple-400 bg-indigo-50 dark:bg-purple-500/10 hover:bg-indigo-100 dark:hover:bg-purple-500/20 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-purple-400"
                      >
                        Acknowledge all
                      </button>
                    )}
                    <button
                      onClick={onClose}
                      className="rounded-md text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-purple-400"
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className="overflow-y-auto max-h-[calc(80vh-80px)]">
                  {loading ? (
                    <div className="flex justify-center items-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 dark:border-purple-500"></div>
                      <p className="ml-3 text-sm text-gray-500 dark:text-gray-400">Loading notifications...</p>
                    </div>
                  ) : notifications.length > 0 ? (
                    <div className="divide-y divide-gray-200 dark:divide-white/10">
                      {notifications.map((notification) => (
                        <div
                          key={notification.id}
                          className={`p-6 hover:bg-gray-50 dark:hover:bg-white/5 cursor-pointer transition-colors ${
                            !notification.read ? 'bg-blue-50 dark:bg-blue-500/10' : ''
                          }`}
                          onClick={async () => {
                            if (!notification.read && markAsRead) {
                              await markAsRead(notification.id);
                            }
                            if (notification.actionUrl && onNavigate) {
                              // Parse the action URL to determine which view to navigate to
                              if (notification.actionUrl.includes('/meetings')) {
                                onNavigate('Meetings');
                              } else if (notification.actionUrl.includes('/profile/reviews')) {
                                onNavigate('User Profile');
                              } else if (notification.actionUrl.includes('/payments')) {
                                onNavigate('Billing & Payments');
                              }
                              // Close the modal after navigation
                              onClose();
                            }
                          }}
                        >
                          <div className="flex items-start space-x-4">
                            <div className="flex-shrink-0">
                              <div className="text-2xl">
                                {notification.icon || '🔔'}
                              </div>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between">
                                <p className={`text-sm font-medium ${
                                  !notification.read ? 'text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-400'
                                }`}>
                                  {notification.title}
                                </p>
                                <div className="flex items-center space-x-2">
                                  <span className="text-xs text-gray-500 dark:text-gray-400">
                                    {formatNotificationTime(notification.createdAt, userTimezone)}
                                  </span>
                                  {notification.priority === 'high' || notification.priority === 'urgent' ? (
                                    <div className="w-2 h-2 bg-red-500 dark:bg-red-400 rounded-full" />
                                  ) : !notification.read ? (
                                    <div className="w-2 h-2 bg-blue-500 dark:bg-blue-400 rounded-full" />
                                  ) : null}
                                </div>
                              </div>
                              <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">
                                {notification.message}
                              </p>
                              {notification.isVerified && (
                                <span className="inline-flex items-center mt-2 px-2 py-0.5 rounded text-xs font-medium bg-green-100 dark:bg-green-500/10 text-green-800 dark:text-green-400">
                                  ✓ Verified
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <BellIcon className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
                      <h3 className="mt-4 text-sm font-medium text-gray-900 dark:text-white">No notifications</h3>
                      <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                        You're all caught up! New notifications will appear here.
                      </p>
                    </div>
                  )}
                </div>
              </DialogPanel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}